import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Bid, ProposalData, CompanyInfo, ClientInfo } from '../types/bidding';
import { formatCurrency, formatBoardFeet, formatPercentage } from './bidCalculator';

// Brand colors
const BRAND_GREEN = '#2C3E2F';
const BRAND_BROWN = '#4F423A';
const BRAND_BEIGE = '#DEDBCE';

/**
 * Generate a PDF proposal from bid data
 * @param proposalData - The proposal data including bid, company info, and client info
 * @returns PDF blob
 */
export async function generateProposalPDF(proposalData: ProposalData): Promise<Blob> {
  const { bid, companyInfo, clientInfo } = proposalData;
  
  // Initialize PDF in portrait mode
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter',
  });
  
  let yPos = 20;
  
  // ====== HEADER ======
  yPos = addHeader(doc, bid, yPos, companyInfo);
  
  // ====== CLIENT INFO ======
  yPos = addClientInfo(doc, yPos, clientInfo);
  
  // ====== EXECUTIVE SUMMARY ======
  yPos = addExecutiveSummary(doc, bid, yPos);
  
  // ====== COST BREAKDOWN BY GROUP ======
  for (const group of bid.groups) {
    // Check if we need a new page
    if (yPos > 240) {
      doc.addPage();
      yPos = 20;
    }
    
    yPos = addGroupBreakdown(doc, group, yPos);
  }
  
  // ====== TERMS & CONDITIONS ======
  if (yPos > 220) {
    doc.addPage();
    yPos = 20;
  }
  yPos = addTermsAndConditions(doc, yPos);
  
  // ====== SIGNATURE SECTION ======
  addSignatureSection(doc, yPos);
  
  // ====== FOOTER ON ALL PAGES ======
  addFooter(doc, bid);
  
  return doc.output('blob');
}

/**
 * Add header with company logo and bid info
 */
function addHeader(doc: jsPDF, bid: Bid, yPos: number, companyInfo?: CompanyInfo): number {
  // Company name
  doc.setFontSize(20);
  doc.setTextColor(BRAND_GREEN);
  doc.setFont('helvetica', 'bold');
  doc.text(companyInfo?.name || 'LCTF', 20, yPos);
  
  // Bid number and date
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.setFont('helvetica', 'normal');
  doc.text(`Bid #: ${bid.bidNumber}`, 160, yPos);
  doc.text(`Date: ${new Date().toLocaleDateString()}`, 160, yPos + 5);
  
  yPos += 15;
  
  // Divider line
  doc.setDrawColor(BRAND_GREEN);
  doc.setLineWidth(0.5);
  doc.line(20, yPos, 195, yPos);
  
  return yPos + 10;
}

/**
 * Add client information
 */
function addClientInfo(doc: jsPDF, yPos: number, clientInfo?: ClientInfo): number {
  if (!clientInfo) return yPos;
  
  doc.setFontSize(12);
  doc.setTextColor(BRAND_GREEN);
  doc.setFont('helvetica', 'bold');
  doc.text('PROPOSAL FOR:', 20, yPos);
  
  yPos += 7;
  
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  doc.setFont('helvetica', 'normal');
  
  if (clientInfo.name) {
    doc.text(clientInfo.name, 20, yPos);
    yPos += 5;
  }
  if (clientInfo.email) {
    doc.text(clientInfo.email, 20, yPos);
    yPos += 5;
  }
  if (clientInfo.phone) {
    doc.text(clientInfo.phone, 20, yPos);
    yPos += 5;
  }
  if (clientInfo.address) {
    doc.text(clientInfo.address, 20, yPos);
    yPos += 5;
  }
  
  return yPos + 8;
}

/**
 * Add executive summary
 */
function addExecutiveSummary(doc: jsPDF, bid: Bid, yPos: number): number {
  doc.setFontSize(14);
  doc.setTextColor(BRAND_GREEN);
  doc.setFont('helvetica', 'bold');
  doc.text('PROJECT SUMMARY', 20, yPos);
  
  yPos += 8;
  
  doc.setFontSize(11);
  doc.setTextColor(60, 60, 60);
  doc.setFont('helvetica', 'normal');
  doc.text(bid.projectName, 20, yPos);
  
  yPos += 8;
  
  // Grand total box
  const grandTotal = bid.groups.reduce((sum, g) => sum + g.total, 0);
  
  doc.setFillColor(BRAND_BEIGE);
  doc.rect(20, yPos, 175, 15, 'F');
  
  doc.setFontSize(12);
  doc.setTextColor(BRAND_GREEN);
  doc.setFont('helvetica', 'bold');
  doc.text('TOTAL INVESTMENT:', 25, yPos + 10);
  
  doc.setFontSize(16);
  doc.text(formatCurrency(grandTotal), 170, yPos + 10, { align: 'right' });
  
  yPos += 22;
  
  return yPos;
}

/**
 * Add group cost breakdown
 */
function addGroupBreakdown(doc: jsPDF, group: any, yPos: number): number {
  // Group header
  doc.setFontSize(13);
  doc.setTextColor(BRAND_GREEN);
  doc.setFont('helvetica', 'bold');
  doc.text(group.name.toUpperCase(), 20, yPos);
  
  yPos += 7;
  
  // Design bucket
  if (group.designBucket.total > 0) {
    yPos = addBucketSection(doc, 'Design', group.designBucket.lineItems, group.designBucket.total, yPos);
  }
  
  // Production bucket
  if (group.productionBucket.total > 0) {
    const productionRows = [
      [
        'Production Labor',
        `${formatBoardFeet(group.productionBucket.totalBoardFeet)} @ $${group.productionBucket.ratePerBoardFoot.toFixed(2)}/BF`,
        '',
        formatCurrency(group.productionBucket.total),
      ],
    ];
    yPos = addBucketSection(doc, 'Production', productionRows, group.productionBucket.total, yPos, true);
  }
  
  // Freight bucket
  if (group.freightBucket.total > 0) {
    const freightRows = [
      ['Inbound Freight', 'To PA shop', '', formatCurrency(group.freightBucket.inbound)],
      ['Outbound Freight', 'To site', '', formatCurrency(group.freightBucket.outbound)],
    ];
    yPos = addBucketSection(doc, 'Freight', freightRows, group.freightBucket.total, yPos, true);
  }
  
  // Install bucket
  if (group.installBucket.total > 0) {
    yPos = addBucketSection(doc, 'Installation', group.installBucket.lineItems, group.installBucket.total, yPos);
  }
  
  // Materials bucket
  if (group.materialsBucket.total > 0) {
    const materialRows: any[] = [];
    
    // Add timber summary
    if (group.materialsBucket.timbers.totalCost > 0) {
      materialRows.push([
        'Timber Materials',
        `${group.timberList.length} items`,
        '',
        formatCurrency(group.materialsBucket.timbers.totalCost),
      ]);
    }
    
    // Add manual items
    group.materialsBucket.manualItems.forEach((item: any) => {
      materialRows.push([
        item.description,
        `${item.quantity} ${item.unit}`,
        formatCurrency(item.unitPrice),
        formatCurrency(item.totalPrice),
      ]);
    });
    
    yPos = addBucketSection(doc, 'Materials', materialRows, group.materialsBucket.total, yPos, true);
  }
  
  // Group total (profit margin is hidden from proposals - internal only)
  yPos += 5;
  
  // Group total - don't show subtotal or margin breakdown to clients
  doc.setFillColor(BRAND_GREEN);
  doc.setTextColor(255, 255, 255);
  doc.rect(20, yPos - 4, 175, 9, 'F');
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(`${group.name.toUpperCase()} TOTAL:`, 100, yPos);
  doc.text(formatCurrency(group.total), 188, yPos, { align: 'right' });
  
  yPos += 12;
  
  return yPos;
}

/**
 * Add a bucket section with table
 */
function addBucketSection(
  doc: jsPDF,
  bucketName: string,
  rows: any[],
  total: number,
  yPos: number,
  isSimple: boolean = false
): number {
  doc.setFontSize(10);
  doc.setTextColor(BRAND_BROWN);
  doc.setFont('helvetica', 'bold');
  doc.text(bucketName, 25, yPos);
  
  yPos += 3;
  
  if (rows.length > 0) {
    const tableData = rows.map((item: any) => {
      if (isSimple) {
        return item; // Already formatted array
      }
      
      // Format install/design line items
      const desc = item.description || item.architect || '';
      const rateQty = item.rate && item.quantity 
        ? `$${item.rate}/${item.rateUnit || 'hr'} × ${item.quantity}`
        : '';
      const cost = item.cost ? formatCurrency(item.cost) : '';
      const billable = item.billableTotal ? formatCurrency(item.billableTotal) : '';
      
      return [desc, rateQty, cost, billable];
    });
    
    autoTable(doc, {
      startY: yPos,
      head: isSimple ? [] : [['Description', 'Rate × Qty', 'Cost', 'Billable']],
      body: tableData,
      theme: 'plain',
      styles: {
        fontSize: 9,
        cellPadding: 2,
      },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 50 },
        2: { cellWidth: 30, halign: 'right' },
        3: { cellWidth: 30, halign: 'right' },
      },
      margin: { left: 30 },
    });
    
    yPos = (doc as any).lastAutoTable.finalY + 3;
  }
  
  // Bucket total
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.setFont('helvetica', 'italic');
  doc.text(`${bucketName} Total:`, 130, yPos);
  doc.text(formatCurrency(total), 188, yPos, { align: 'right' });
  
  yPos += 8;
  
  return yPos;
}

/**
 * Add terms and conditions
 */
function addTermsAndConditions(doc: jsPDF, yPos: number): number {
  doc.setFontSize(12);
  doc.setTextColor(BRAND_GREEN);
  doc.setFont('helvetica', 'bold');
  doc.text('TERMS & CONDITIONS', 20, yPos);
  
  yPos += 7;
  
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);
  doc.setFont('helvetica', 'normal');
  
  const terms = [
    'Payment: 50% deposit required upon acceptance, balance due upon completion.',
    'Timeline: Work will commence within 2 weeks of deposit receipt.',
    'Warranty: All timber materials and workmanship are warranted for 1 year.',
    'Revisions: Any changes to scope may result in additional charges.',
    'Validity: This proposal is valid for 30 days from the date above.',
  ];
  
  terms.forEach(term => {
    doc.text(`• ${term}`, 25, yPos);
    yPos += 6;
  });
  
  return yPos + 5;
}

/**
 * Add signature section
 */
function addSignatureSection(doc: jsPDF, yPos: number): void {
  yPos += 10;
  
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  doc.setFont('helvetica', 'normal');
  
  // Client signature
  doc.text('Client Signature:', 20, yPos);
  doc.line(50, yPos, 100, yPos);
  doc.text('Date:', 110, yPos);
  doc.line(125, yPos, 155, yPos);
  
  yPos += 15;
  
  // Company signature
  doc.text('LCTF Representative:', 20, yPos);
  doc.line(50, yPos, 100, yPos);
  doc.text('Date:', 110, yPos);
  doc.line(125, yPos, 155, yPos);
}

/**
 * Add footer to all pages
 */
function addFooter(doc: jsPDF, bid: Bid): void {
  const pageCount = doc.getNumberOfPages();
  
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.setFont('helvetica', 'normal');
    
    // Page number
    doc.text(`Page ${i} of ${pageCount}`, 105, 285, { align: 'center' });
    
    // Bid number
    doc.text(`${bid.bidNumber}`, 20, 285);
    
    // Generated date
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 188, 285, { align: 'right' });
  }
}

/**
 * Download PDF to user's computer
 */
export function downloadProposalPDF(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
